/**
 * The team's two moves on a send the client has not answered (chapter 08
 * frames rxC0N and FKvo5): remind them (backend #130), or record an approval
 * they gave outside CreativeOS (backend #129). Both are optimistic.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { axiosInstance } from "@/lib/api";
import { queue } from "@/components/Toast";
import type {
  AcceptIdeaResult,
  CalendarPlanSlot,
  ClientReminder,
  OutsideApprovalChannel,
  SlotOutsideApproval,
} from "@/types/contentCalendar.type";
import type { BriefComment, ProductionBrief } from "@/types/productionBrief.type";
import { useAuthStore } from "@/stores/authStore";
import { cancelPlanFetches, patchSlot, patchSlotsWhere, planWriteKey, putSlot, readPlan, readSlot, settlePlanWrite } from "../api/calendarCache";
import { acceptBriefWithPost, planBriefsKey, slotBriefKey } from "../api/useProductionBrief";

export type ReviewRoundKind = "concept" | "brief";

function errorMessage(err: unknown, fallback: string): string {
  if (isAxiosError(err)) {
    return (err.response?.data as { message?: string })?.message ?? err.message ?? fallback;
  }
  return fallback;
}

// ------------------------------------------------------------ remind

export interface RemindInput {
  slotId: string;
  kind: ReviewRoundKind;
  /** `clientReview[kind].roundId` from the plan read. */
  roundId: string;
  /** Send a second reminder inside the 24 hour cooldown. */
  force?: boolean;
}

interface RemindResult {
  reminder: ClientReminder;
  notified: number;
  failed: number;
  failures: { userId: string; email: string }[];
}

/** The 409 the cooldown answers with: "The client was reminded … Confirm to send another." */
export function isReminderCooldown(err: unknown): string | null {
  if (!isAxiosError(err) || err.response?.status !== 409) return null;
  return (err.response.data as { message?: string })?.message ?? "They were reminded in the last day.";
}

/**
 * POST plans/:planId/remind. The last reminder shows at once on every post in
 * the send; a 409 inside the cooldown rolls back and is handed to the caller
 * (`isReminderCooldown`) to confirm and resend with `force`.
 */
export function useRemindClient(planId: string | null) {
  const qc = useQueryClient();
  const byName = useAuthStore((s) => s.user?.name)?.trim() || null;
  return useMutation({
    mutationKey: [...planWriteKey(planId), "remind"],
    mutationFn: async ({ kind, roundId, force }: RemindInput) => {
      const res = await axiosInstance.post<RemindResult>(`/content-calendar/plans/${planId}/remind`, {
        kind,
        roundId,
        ...(force ? { force: true } : {}),
      });
      return res.data;
    },
    onMutate: async ({ kind, roundId }) => {
      await cancelPlanFetches(qc, planId);
      const inSend = (slot: CalendarPlanSlot) => slot.clientReview?.[kind]?.roundId === roundId;
      // The posts in this send as they were: the narrow undo.
      const previous = (readPlan(qc, planId)?.slots ?? []).filter(inSend);
      const reminder: ClientReminder = { sentAt: new Date().toISOString(), automatic: false, byName, recipients: 0 };
      patchSlotsWhere(
        qc,
        planId,
        inSend,
        (slot) => ({
          ...slot,
          clientReview: { concept: null, brief: null, ...slot.clientReview, [kind]: { ...slot.clientReview![kind]!, lastReminder: reminder } },
        }),
      );
      return { previous };
    },
    onSuccess: (result) => {
      queue.add({
        title: result.failed > 0 ? `Reminded ${result.notified}, ${result.failed} could not be reached` : "Reminder sent",
        description:
          result.failed > 0
            ? `Check the address for ${result.failures.map((f) => f.email).join(", ")}.`
            : `${result.notified} ${result.notified === 1 ? "person was" : "people were"} emailed.`,
        variant: result.failed > 0 ? "error" : "success",
      });
    },
    onError: (err, _input, context) => {
      for (const slot of context?.previous ?? []) putSlot(qc, planId, slot);
      // The cooldown is a question for the caller, not an error.
      if (isReminderCooldown(err)) return;
      queue.add({ title: "Could not send the reminder", description: errorMessage(err, "Please try again."), variant: "error" });
    },
    onSettled: () => settlePlanWrite(qc, planId, { refetch: true }),
  });
}

// ------------------------------------------------------------ outside approval

export interface OutsideApprovalInput {
  slotId: string;
  kind: ReviewRoundKind;
  /** Who approved, as they should read on the post. */
  name: string;
  channel: OutsideApprovalChannel;
  /** ISO timestamp; never in the future. */
  approvedAt: string;
  note?: string | null;
}

type SlotBriefEntry = { brief: ProductionBrief | null; comments: BriefComment[] };

/**
 * Record an approval the client gave outside CreativeOS: the team approves the
 * concept (plan-slots/:id/approve) or accepts the brief (…/brief/accept) with
 * an `outside` record. The post moves on at once, as if they had pressed
 * Approve; a failure puts it back.
 */
export function useRecordOutsideApproval(planId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: [...planWriteKey(planId), "outsideApproval"],
    mutationFn: async (input: OutsideApprovalInput) => {
      const outside = {
        name: input.name.trim(),
        channel: input.channel,
        approvedAt: input.approvedAt,
        ...(input.note?.trim() ? { note: input.note.trim() } : {}),
      };
      if (input.kind === "concept") {
        const res = await axiosInstance.post<AcceptIdeaResult>(
          `/content-calendar/plan-slots/${input.slotId}/approve`,
          { outside },
        );
        return { kind: "concept" as const, slot: res.data.slot };
      }
      const res = await axiosInstance.post<{ brief: ProductionBrief }>(
        `/content-calendar/plan-slots/${input.slotId}/brief/accept`,
        { outside },
      );
      return { kind: "brief" as const, brief: res.data.brief };
    },
    onMutate: async (input) => {
      const record: SlotOutsideApproval = {
        approvedByName: input.name.trim(),
        channel: input.channel,
        approvedAt: input.approvedAt,
        note: input.note?.trim() || null,
      };
      await cancelPlanFetches(qc, planId);
      const slot = readSlot(qc, planId, input.slotId);
      const outsideApproval = { concept: slot?.outsideApproval?.concept ?? null, brief: slot?.outsideApproval?.brief ?? null };
      const clientReview = { concept: slot?.clientReview?.concept ?? null, brief: slot?.clientReview?.brief ?? null };
      let previousBrief: SlotBriefEntry | undefined;
      let undoBrief: (() => void) | undefined;
      // A post whose idea is not approved yet: one answer approves the idea and
      // its brief (brief/accept does the same server-side), recorded on both.
      if (input.kind === "concept" || (slot && slot.status !== "CLIENT_APPROVED")) {
        patchSlot(qc, planId, input.slotId, (s) => ({
          ...s,
          status: "CLIENT_APPROVED",
          approvalSource: "TEAM",
          outsideApproval: { ...outsideApproval, concept: record, brief: record },
          clientReview: { concept: null, brief: null },
          updatedAt: new Date().toISOString(),
        }));
        undoBrief = await acceptBriefWithPost(qc, planId, input.slotId);
      } else {
        await qc.cancelQueries({ queryKey: slotBriefKey(input.slotId) });
        previousBrief = qc.getQueryData<SlotBriefEntry>(slotBriefKey(input.slotId));
        qc.setQueryData<SlotBriefEntry>(slotBriefKey(input.slotId), (prev) =>
          prev?.brief ? { ...prev, brief: { ...prev.brief, status: "ACCEPTED", acceptanceSource: "TEAM" } } : prev,
        );
        patchSlot(qc, planId, input.slotId, (s) => ({
          ...s,
          outsideApproval: { ...outsideApproval, brief: record },
          clientReview: { ...clientReview, brief: null },
        }));
      }
      return { slot, previousBrief, undoBrief };
    },
    onSuccess: (result, input) => {
      if (result.kind === "brief") qc.setQueryData<SlotBriefEntry>(slotBriefKey(input.slotId), (prev) => (prev ? { ...prev, brief: result.brief } : prev));
      else void qc.invalidateQueries({ queryKey: slotBriefKey(input.slotId) });
      queue.add({
        title: "Approval recorded",
        description: `Approved by ${input.name.trim()}. The post moves on.`,
        variant: "success",
      });
    },
    onError: (err, input, context) => {
      if (context?.slot) putSlot(qc, planId, context.slot);
      if (context?.previousBrief) qc.setQueryData(slotBriefKey(input.slotId), context.previousBrief);
      context?.undoBrief?.();
      queue.add({ title: "Could not record the approval", description: errorMessage(err, "Please try again."), variant: "error" });
    },
    onSettled: (result, err) => {
      settlePlanWrite(qc, planId, !err && result?.kind === "concept" ? { slot: result.slot } : { refetch: true });
      void qc.invalidateQueries({ queryKey: planBriefsKey(planId) });
    },
  });
}
